'use client';

import {
  TeamClaimStatus,
  type TeamVerificationMessageProps,
} from '@baseball-near-you/common';
import { Button, Separator, toast } from '@baseball-near-you/ui';
import { TeamAddStep } from '@bb/components/Onboarding/steps';
import { clients } from '@bb/lib/clients';
import { QueryKey } from '@bb/lib/hooks/QueryKey';
import { useSpecificTeam } from '@bb/lib/hooks/useSpecificTeam';
import { useUserProfile } from '@bb/lib/hooks/useUserProfile';
import { getInitials } from '@bb/lib/utils/getInitials';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, ChevronLeft, XCircle } from 'lucide-react';
import Image from 'next/image';
import { useState } from 'react';
import { ClaimCard } from './ClaimCard';
import { NoTeamsMessage } from './NoTeamsMessage';
import { TeamSelectionModal } from './TeamSelectionModal';

export function useClaimTeamMutation() {
  const queryClient = useQueryClient();
  const { mutateAsync: handleClaimTeam, isLoading: claimTeamLoading } =
    useMutation({
      mutationFn: async (args: TeamVerificationMessageProps) => {
        return clients.team.claimTeam({
          args: {
            teamId: Number(args.teamId),
          },
          payload: args,
        });
      },
      onSuccess: async (args) => {
        await queryClient.invalidateQueries([QueryKey.User]);
        toast.success('Your claim has been submitted!', {
          description: "We'll get back to you soon!",
          icon: <CheckCircle size={14} />,
        });
      },
    });
  return { handleClaimTeam, claimTeamLoading };
}

export const claimStatusStyleMap: Record<TeamClaimStatus, string> = {
  [TeamClaimStatus.PENDING]: 'bg-yellow-400/[12%]',
  [TeamClaimStatus.APPROVED]: 'bg-green-400/[20%]',
  [TeamClaimStatus.REJECTED]: 'bg-red/[8%]',
};

export function ClaimTeam() {
  const { userProfile } = useUserProfile();
  const [selectedTeamSlug, setSelectedTeamSlug] = useState<string | null>(null);
  const { team } = useSpecificTeam(selectedTeamSlug);
  const [ratio, setRatio] = useState(1);
  const [isAddingTeam, setIsAddingTeam] = useState(false);

  return (
    <div className="flex flex-col">
      {selectedTeamSlug && (
        <div className="rounded-md border bg-gray-100 p-3">
          <div className="flex justify-between">
            <p className="mb-4 font-semibold text-lg">Selected team</p>
            {selectedTeamSlug && (
              <Button
                className="border bg-red/25 p-3 "
                onClick={() => setSelectedTeamSlug(null)}
              >
                <XCircle size={14} />
              </Button>
            )}
          </div>
          <div className="flex space-x-5">
            <div
              className="relative w-16"
              style={{
                aspectRatio: ratio,
              }}
            >
              {team?.logoUrl ? (
                <Image
                  alt="team logo"
                  fill
                  src={team?.logoUrl}
                  onLoad={(img) => {
                    setRatio(
                      img.currentTarget.naturalWidth /
                        img.currentTarget.naturalHeight
                    );
                  }}
                />
              ) : (
                <div className="flex size-16 items-center justify-center rounded-full border bg-gray-50">
                  <p className="title text-xl">
                    {getInitials(team?.name || '')}
                  </p>
                </div>
              )}
            </div>
            <div className="flex-col">
              <p className="font-semibold text-gray-600 text-lg">
                {team?.name}
              </p>
              {team?.address[0]?.city && team?.address[0].state && (
                <p className="font-medium text-gray-600">
                  {team?.address[0].city}, {team?.address[0].state}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      {isAddingTeam ? (
        <>
          <Separator className="mb-3 flex w-full flex-col" />
          <div className="mb-3 flex items-center justify-start gap-x-2.5 font-semibold text-gray-900 text-xl">
            <button
              onClick={() => setIsAddingTeam(false)}
              type="button"
              className="flex items-center gap-2 rounded-sm border border-gray-300 bg-gray-100 p-1 transition-colors hover:bg-gray-200"
            >
              <ChevronLeft size={20} />
            </button>
            <p className="mt-0.5">Add your team to our platform</p>
          </div>
          <p className="mb-5 text-left text-base text-gray-800">
            Help us learn more about your team by providing the information
            below.{' '}
            <span className="font-medium">
              Once submitted, we'll review your request and send you an email
              with next steps.
            </span>
          </p>
          <TeamAddStep
            onStepSuccess={() => {
              setIsAddingTeam(false);
              toast.success('Your team creation request has been submitted!', {
                description: 'You will hear from us soon about next steps.',
              });
            }}
          />
        </>
      ) : (
        <TeamSelectionModal
          noResultsNode={
            <NoTeamsMessage
              onAddTeam={() => {
                setIsAddingTeam(true);
              }}
            />
          }
        />
      )}
      {isAddingTeam ? null : (
        <>
          <Separator className="my-6 flex w-full flex-col" />
          {userProfile && userProfile.claims.length > 0 ? (
            <>
              <div className="font-semibold">
                Past request
                {userProfile?.claims.length > 1 ? 's' : ''}:
              </div>
              <div className="h-[28rem] space-y-3 overflow-auto border-b py-2 shadow-xs">
                {userProfile.claims.map((claim) => (
                  <ClaimCard claim={claim} key={claim.id} />
                ))}
              </div>
            </>
          ) : (
            <>
              <p className="mb-1 text-left font-semibold text-base">
                You currently do not have any teams claimed.
              </p>
              <p className="text-left text-base text-gray-500">
                Request access to a team to gain the ability to give your
                players the latest information about training, tryouts, and
                more!
              </p>
            </>
          )}
        </>
      )}
    </div>
  );
}
